import React, { useState, useCallback } from 'react';
import { Box, Typography, LinearProgress, Alert, Button, useTheme } from '@mui/joy';
import { useMediaQuery } from '@mui/material';
import { uploadVideoWithPresignedUrl } from './functions/uploadVideoWithPresignedUrl';
import { updateDocumentMetadata } from '../../apiconfig/api';
import { v4 as uuidv4 } from 'uuid';

interface ResumeUploadProps {
  onUploadComplete: (documentId: string) => void;
  onUploadError: (error: string) => void;
  disabled?: boolean;
}

const ResumeUpload: React.FC<ResumeUploadProps> = ({ 
  onUploadComplete, 
  onUploadError, 
  disabled = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, [disabled, isUploading]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || isUploading) return;

    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, [disabled, isUploading]);
  
  const handleMobileButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.txt';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    };
    input.click();
  };

  const handleFileUpload = async (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      onUploadError('Please upload a PDF, Word document, or text file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      onUploadError('File size must be less than 10MB.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Create document metadata and get upload URL
      const documentId = uuidv4();
      
      // Get file extension from original filename
      const originalExtension = file.name.split('.').pop() || '';
      
      // Create filename in format: documentId.extension
      const filename = `${documentId}.${originalExtension}`;
      
      const metadataPayload = {
        id: documentId,
        originalFileKey: filename,
        originalFileMimeType: file.type,
        metadata: {
          metadataVersion: new Date().toISOString().split("T")[0],
          source: "upload",
          uploadDevice: {
            name: navigator.userAgent,
            manufacturer: null,
            model: null,
            version: null,
            os: null,
            osVersion: null,
          },
        },
      };

      const documentResult = await updateDocumentMetadata(documentId, metadataPayload);
      const uploadUrl = (documentResult as any).data?.uploadUrl || (documentResult as any).uploadUrl;

      if (!uploadUrl) {
        throw new Error("Upload URL is missing from document metadata response.");
      }

      // Upload the document using the URL from document metadata
      await uploadVideoWithPresignedUrl({
        file,
        uploadUrl: uploadUrl,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      setUploadedFileName(file.name);
      setUploadProgress(100);
      setIsUploading(false);
      onUploadComplete(documentId);
    } catch (error) {
      console.error("Document upload failed:", error);
      setIsUploading(false);
      setUploadProgress(0);
      onUploadError("Failed to upload resume. Please try again.");
    }
  };

  if (isMobile) {
    // Mobile: Simple button interface
    return (
      <Box>
        <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
          Add a Resume (Optional)
        </Typography>
        
        {isUploading ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Uploading resume...
            </Typography>
            <LinearProgress 
              determinate
              value={uploadProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#2e2d7e',
                }
              }} 
            />
            <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
              {uploadProgress}% complete
            </Typography>
          </Box>
        ) : (
          <Button
            variant="outlined"
            onClick={handleMobileButtonClick}
            disabled={disabled || isUploading}
            sx={{
              width: '100%',
              py: 1.5,
              borderColor: uploadedFileName ? '#10B981' : '#9CA3AF',
              color: uploadedFileName ? '#10B981' : '#6B7280',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: '12px',
              borderStyle: uploadedFileName ? 'solid' : 'dashed',
              '&:hover': {
                borderColor: uploadedFileName ? '#059669' : '#6B7280',
                color: uploadedFileName ? '#059669' : '#4B5563',
                bgcolor: uploadedFileName ? 'rgba(16, 185, 129, 0.04)' : 'rgba(107, 114, 128, 0.04)',
              },
            }}
          >
            {uploadedFileName ? `✓ ${uploadedFileName}` : 'Select Resume'}
          </Button>
        )}
      </Box>
    );
  }

  // Desktop: Full drag and drop interface
  return (
    <Box>
      <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
        Add a Resume (Optional)
      </Typography>

      <Box
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? '#2e2d7e' : '#E5E7EB',
          borderRadius: '12px',
          p: 3,
          textAlign: 'center',
          backgroundColor: dragActive ? '#F8F9FA' : 'transparent',
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          opacity: disabled || isUploading ? 0.6 : 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#2e2d7e',
            backgroundColor: '#F8F9FA',
          },
        }}
      >
        {isUploading ? (
          <Box>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Uploading resume...
            </Typography>
            <LinearProgress 
              determinate
              value={uploadProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#2e2d7e',
                }
              }} 
            />
            <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
              {uploadProgress}% complete
            </Typography>
          </Box>
        ) : uploadedFileName ? (
          <Box>
            <Typography level="body-md" sx={{ color: '#10B981', fontWeight: 500 }}>
              ✓ Resume uploaded successfully
            </Typography>
            <Typography level="body-sm" sx={{ color: '#7B7F85', mt: 1 }}>
              {uploadedFileName}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography level="body-md" sx={{ mb: 1 }}>
              Drag and drop your resume here, or click to browse
            </Typography>
            <Typography level="body-sm" sx={{ color: '#7B7F85' }}>
              Supports PDF, Word documents, and text files (max 10MB)
            </Typography>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="resume-upload-input"
              disabled={disabled || isUploading}
            />
            <label htmlFor="resume-upload-input">
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mt: 2,
                  px: 3,
                  py: 1,
                  backgroundColor: '#2e2d7e',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#1e1d6e',
                  },
                }}
              >
                Choose File
              </Box>
            </label>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResumeUpload; 